import { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";

/**
 * Manages the full OTP email-verification state machine.
 *
 * Steps:
 *   idle       — not started
 *   sending    — calling POST /otp/send
 *   awaiting   — code sent, waiting for user input
 *   verifying  — calling POST /otp/verify
 *   verified   — email confirmed; verifiedToken is available
 *
 * Usage:
 *   const otp = useOtpVerification();
 *
 *   // 1. Trigger OTP send (call from form submit handler, after validation)
 *   await otp.send(email, name);
 *
 *   // 2. OtpVerifyPanel takes the otp object and calls otp.verify(code)
 *
 *   // 3. Once otp.step === "verified", include otp.verifiedToken in the POST body
 *
 *   // 4. To reset (e.g. after success or on close): otp.reset()
 */
export function useOtpVerification() {
  const [step,          setStep]          = useState("idle");
  const [token,         setToken]         = useState(null);   // OTP signing token
  const [verifiedToken, setVerifiedToken] = useState(null);   // proof of ownership
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [error,         setError]         = useState("");
  const [resendTimer,   setResendTimer]   = useState(0);
  const timerRef = useRef(null);

  // Countdown for resend cooldown
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [resendTimer]);

  /**
   * Send a verification code to `email`.
   * Transitions: idle/awaiting → sending → awaiting
   */
  async function send(email, name = "there") {
    setStep("sending");
    setError("");
    try {
      const res  = await fetch(`${API_URL}/otp/send`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: email.trim().toLowerCase(), name }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed to send code.");
      setToken(json.token);
      setResendTimer(60);
      setStep("awaiting");
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
      setStep("idle");
    }
  }

  /**
   * Verify the code the user entered.
   * Transitions: awaiting → verifying → verified | back to awaiting on failure
   * @returns {boolean} true if verified successfully
   */
  async function verify(code) {
    setStep("verifying");
    setError("");
    try {
      const res  = await fetch(`${API_URL}/otp/verify`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, code: String(code).trim() }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || "Incorrect code. Please try again.");
        setStep("awaiting");
        return false;
      }
      setVerifiedToken(json.verifiedToken);
      setVerifiedEmail(json.email);
      setStep("verified");
      return true;
    } catch {
      setError("Network error. Please try again.");
      setStep("awaiting");
      return false;
    }
  }

  /** Resend the code (respects 60-second cooldown). */
  async function resend(email, name) {
    if (resendTimer > 0) return;
    setError("");
    await send(email, name);
  }

  /** Reset to initial state (call on modal close or after success). */
  function reset() {
    clearTimeout(timerRef.current);
    setStep("idle");
    setToken(null);
    setVerifiedToken(null);
    setVerifiedEmail(null);
    setError("");
    setResendTimer(0);
  }

  return {
    step,           // "idle" | "sending" | "awaiting" | "verifying" | "verified"
    token,
    verifiedToken,
    verifiedEmail,
    error,
    resendTimer,
    send,
    verify,
    resend,
    reset,
    setError,
  };
}
